import { useState, useEffect, useCallback, useMemo } from 'react'
import { apiService } from '@/services/api'

export type UnreadCounts = {
  [userId: string]: number
}

type UnreadCountsData = {
  unread_counts: UnreadCounts
  total_unread: number
}

interface UseUnreadCountsProps {
  tableId: string | null
  currentUserId: string | null
  enabled?: boolean
  refreshInterval?: number
}

export function useUnreadCounts({
  tableId,
  currentUserId,
  enabled = true,
  refreshInterval = 30000 // 30 segundos
}: UseUnreadCountsProps) {
  const [unreadCounts, setUnreadCounts] = useState<UnreadCounts>({})
  // Estado derivado: soma dos contadores. Antes era state proprio atualizado
  // dentro dos updaters de setUnreadCounts (updater impuro — em StrictMode
  // roda 2x e o total divergia).
  const totalUnread = useMemo(
    () => Object.values(unreadCounts).reduce((sum, count) => sum + count, 0),
    [unreadCounts]
  )
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchUnreadCounts = useCallback(async () => {
    if (!enabled || !tableId || !currentUserId) {
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // apiService.request centraliza token + tratamento de 401 (sessao
      // expirada) — antes era fetch cru com token do localStorage.
      const data = await apiService.request<UnreadCountsData>(
        `/tables/${tableId}/unread-counts`
      )

      setUnreadCounts(data.unread_counts || {})

    } catch (err) {
      console.error('Erro ao buscar contagens não lidas:', err)
      setError(err instanceof Error ? err.message : 'Erro desconhecido')
    } finally {
      setIsLoading(false)
    }
  }, [tableId, currentUserId, enabled])

  // Função para marcar mensagens de um usuário como lidas localmente
  const markUserMessagesAsRead = useCallback((userId: string) => {
    setUnreadCounts(prev => {
      if (!(userId in prev)) return prev
      const newCounts = { ...prev }
      delete newCounts[userId]
      return newCounts
    })
  }, [])

  // Função para incrementar contagem de um usuário
  const incrementUnreadCount = useCallback((userId: string) => {
    setUnreadCounts(prev => ({
      ...prev,
      [userId]: (prev[userId] || 0) + 1
    }))
  }, [])

  // Buscar contagens iniciais
  useEffect(() => {
    fetchUnreadCounts()
  }, [fetchUnreadCounts])

  // Polling para atualizar contagens periodicamente
  useEffect(() => {
    if (!enabled || refreshInterval <= 0) {
      return
    }

    const interval = setInterval(() => {
      fetchUnreadCounts()
    }, refreshInterval)

    return () => clearInterval(interval)
  }, [fetchUnreadCounts, enabled, refreshInterval])

  return {
    unreadCounts,
    totalUnread,
    isLoading,
    error,
    fetchUnreadCounts,
    markUserMessagesAsRead,
    incrementUnreadCount
  }
}